import { getAuthToken } from '../utils/auth';

const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') ?? '';

export type OrderUploadType = 'inspection' | 'technician' | 'delivery';
export type OrderFileType = OrderUploadType;

export interface OrderFileRecord {
  id: string;
  orderId: string;
  type: OrderFileType;
  s3Key: string;
  url: string;
  originalName: string | null;
  contentType: string | null;
  size: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function uploadOrderFile(input: { orderId: string; type: OrderUploadType; file: File; filename?: string }) {
  const form = new FormData();
  form.append('file', input.file);
  form.append('orderId', input.orderId);
  form.append('type', input.type);
  if (input.filename) {
    form.append('filename', input.filename);
  }

  const token = getAuthToken();

  const res = await fetch(`${baseUrl}/files/upload/order`, {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }

  return (await res.json()) as { ok: boolean; data?: any };
}

export async function uploadOrderFiles(input: { orderId: string; type: OrderUploadType; files: File[] | FileList }) {
  const form = new FormData();
  const filesArray: File[] = Array.isArray(input.files)
    ? (input.files as File[])
    : Array.from<File>(input.files as FileList);
  filesArray.forEach((f) => form.append('files', f));
  form.append('orderId', input.orderId);
  form.append('type', input.type);

  const token = getAuthToken();
  const res = await fetch(`${baseUrl}/files/upload/order/many`, {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return (await res.json()) as { ok: boolean; data?: any[] };
}

export async function fetchOrderFiles(orderId: string, type?: OrderFileType) {
  const token = getAuthToken();
  const params = type ? `?type=${encodeURIComponent(type)}` : '';
  const res = await fetch(`${baseUrl}/files/orders/${orderId}/files${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  const body = await res.json();
  return (body?.data ?? []) as OrderFileRecord[];
}

export async function deleteOrderFile(orderId: string, fileId: string, s3Key?: string) {
  const token = getAuthToken();
  // Primary: body-based delete to avoid proxy/method issues
  const primary = await fetch(`${baseUrl}/files/delete`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, fileId, s3Key }),
  });
  if (primary.ok) return true;
  // Fallback: try DELETE, then POST path variant
  const res = await fetch(`${baseUrl}/files/orders/${orderId}/files/${fileId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (res.ok) return true;
  const alt = await fetch(`${baseUrl}/files/orders/${orderId}/files/${fileId}/delete`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!alt.ok) {
    const text = await alt.text();
    throw new Error(text || alt.statusText);
  }
  return true;
}
